import type { ComponentPropsWithoutRef, ComponentRef, ElementType, HTMLAttributes, Ref, RefAttributes } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import clsx from 'clsx'
import cn from '@components/utils/cn'
import { preventEscapeForOpenPopup } from '@components/utils/preventEscapeForOpenPopup'

// import { Button } from '@atoms'
// import Icon from '@components/utils/Icon'

const Sheet = DialogPrimitive.Root

const SheetTrigger: ElementType<DialogPrimitive.DialogTriggerProps & RefAttributes<HTMLButtonElement>> =
  DialogPrimitive.Trigger

const SheetClose: ElementType<DialogPrimitive.DialogTriggerProps & RefAttributes<HTMLButtonElement>> =
  DialogPrimitive.Close

const SheetPortal: ElementType<
  { container: any } & DialogPrimitive.DialogTriggerProps & RefAttributes<HTMLButtonElement>
> = DialogPrimitive.Portal

const SheetOverlay = ({
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
  ref?: Ref<ComponentRef<typeof DialogPrimitive.Overlay>>
}) => (
  <DialogPrimitive.Overlay
    className={clsx(
      'tw:fixed tw:inset-0 tw:z-50 tw:bg-black/60',
      'tw:data-[state=open]:animate-in tw:data-[state=open]:fade-in-0',
      'tw:data-[state=closed]:animate-out tw:data-[state=closed]:fade-out-0'
    )}
    {...props}
    ref={ref}
  />
)

type SheetContentProps = { portalContainer?: any; side?: 'left' | 'right'; bgClassName?: string } & ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> & {
    ref?: Ref<ComponentRef<typeof DialogPrimitive.Content>>
  }

const SheetContent = ({
  children,
  portalContainer,
  side = 'right',
  className,
  bgClassName = 'tw:bg-white',
  onEscapeKeyDown,
  ref,
  ...props
}: SheetContentProps) => (
  <SheetPortal container={portalContainer}>
    <div className="tailwind">
      <SheetOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'tw:fixed tw:z-50 tw:overflow-y-auto',
          bgClassName,
          'tw:inset-y-0 tw:h-full tw:w-full tw:sm:w-2/3',
          side === 'left'
            ? 'tw:left-0 tw:data-[state=open]:slide-in-from-left tw:data-[state=closed]:slide-out-to-left'
            : 'tw:right-0 tw:data-[state=open]:slide-in-from-right tw:data-[state=closed]:slide-out-to-right',
          'tw:data-[state=open]:animate-in tw:data-[state=closed]:animate-out',
          'tw:transition-none tw:duration-300 tw:ease-in-out',
          'tw:gap-4 tw:shadow-lg',
          className
        )}
        onEscapeKeyDown={(event) => {
          preventEscapeForOpenPopup(event)
          onEscapeKeyDown?.(event)
        }}
        {...props}
      >
        <div>{children}</div>
      </DialogPrimitive.Content>
    </div>
  </SheetPortal>
)

const SheetHeader = ({ ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={clsx('tw:flex tw:flex-col tw:space-y-2 tw:text-center tw:sm:text-left')} {...props} />
)
SheetHeader.displayName = 'SheetHeader'

const SheetFooter = ({ ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={clsx('tw:flex tw:flex-col-reverse tw:sm:flex-row tw:sm:justify-end tw:sm:space-x-2')} {...props} />
)
SheetFooter.displayName = 'SheetFooter'

const SheetTitle = ({
  children,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {
  ref?: Ref<ComponentRef<typeof DialogPrimitive.Title>>
}) => (
  <DialogPrimitive.Title ref={ref} {...props}>
    <header className="tw:border-b tw:border-gray-100 tw:py-7 tw:px-6 tw:bg-linear-to-b tw:from-primary-700 tw:to-primary-600">
      {/* <div className="tw:bg-linear-to-b tw:from-primary-700 tw:to-primary-600 tw:h-2"></div> */}
      <div className="tw:h-full tw:flex tw:items-center">
        <div>
          <h2 className="tw:text-xl tw:font-bold tw:text-white">{children}</h2>
        </div>
      </div>
    </header>
  </DialogPrimitive.Title>
)

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  // SheetDescription,
}
